import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";

import { useSafeAreaInsets } from "@luce/core/hooks";

import { Typography } from "../../atoms";
import { cn } from "../../lib";
import {
  BottomSheetDescriptionPrimitive,
  BottomSheetRootPrimitive,
  BottomSheetTitlePrimitive,
  Box,
  Pressable,
  type BottomSheetSnapPoint,
  type BottomSheetSnapPoints,
} from "../../primitives";
import { SheetScrollBody } from "../../primitives/bottom-sheet/sheet-scroll-body";
import { isContentSized, resolveSnapPoints } from "../../primitives/bottom-sheet/snap-points";
import { spacing } from "../../tokens";
import type {
  BottomSheetBodyProps,
  BottomSheetCloseProps,
  BottomSheetCtxValue,
  BottomSheetDescriptionProps,
  BottomSheetDetent,
  BottomSheetFooterProps,
  BottomSheetHeaderAlign,
  BottomSheetHeaderProps,
  BottomSheetRootProps,
  BottomSheetTitleProps,
} from "./types";

const Ctx = createContext<BottomSheetCtxValue | null>(null);

/** The enclosing Header's alignment, which Title and Description follow. */
const HeaderAlignCtx = createContext<BottomSheetHeaderAlign>("left");

/** The Header's divider (always) and the Footer's (while the Body scrolls), as Dialog's. */
const dividerColor = "border-luce-neutral-border-mediumEmphasis";

const useCtx = () => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("BottomSheet compound parts must be rendered inside <BottomSheet.Root>");
  }
  return ctx;
};

/**
 * Resolve detents to the primitive's snap points, the same format on both
 * platforms: `"max"` -> `1` (the full surface), fractions unchanged, and
 * `["content"]` as is. Invalid detents fall back to `["content"]` here, so the
 * detent indices the sheet reports match what it shows (index 0).
 */
const detentsToSnapPoints = (detents: readonly BottomSheetDetent[]): BottomSheetSnapPoints =>
  resolveSnapPoints(detents.map((d) => (d === "max" ? 1 : d)));

const Root = ({
  open,
  onOpenChange,
  detents = ["content"],
  defaultDetent = 0,
  detent: controlledDetent,
  onDetentChange,
  dismissible = true,
  overlay,
  showHandle = true,
  applySafeArea = true,
  avoidKeyboard = true,
  handleOnly = false,
  haptics = true,
  accessibilityLabel,
  className,
  testID,
  children,
}: BottomSheetRootProps) => {
  // Key on the detent values, not the array identity — consumers commonly pass
  // `detents` as an inline literal, which would otherwise churn the memo (and
  // the native gesture) on every render.
  const detentsKey = detents.join(",");
  const snapPoints = useMemo(
    () => detentsToSnapPoints(detents),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detentsKey],
  );

  // `detent` is the controlled value; `uncontrolledDetent` backs the
  // uncontrolled case so drag-snaps still move the active detent.
  const [uncontrolledDetent, setUncontrolledDetent] = useState(defaultDetent);
  // A content-sized sheet (including an invalid list's fallback) has one detent
  const activeIndex = isContentSized(snapPoints) ? 0 : (controlledDetent ?? uncontrolledDetent);
  const activeSnapPoint = snapPoints[activeIndex] ?? null;

  const setActiveSnapPoint = useCallback(
    (snap: BottomSheetSnapPoint | null) => {
      if (snap == null) return;
      const nextIndex = snapPoints.findIndex((s) => s === snap);
      if (nextIndex < 0 || nextIndex === activeIndex) return;
      if (controlledDetent == null) setUncontrolledDetent(nextIndex);
      onDetentChange?.(nextIndex);
    },
    [onDetentChange, snapPoints, activeIndex, controlledDetent],
  );

  const requestClose = useCallback(() => onOpenChange?.(false), [onOpenChange]);
  const contentSized = isContentSized(snapPoints);
  const [bodyOverflowing, setBodyOverflowing] = useState(false);
  const [hasBody, setHasBody] = useState(false);

  const ctxValue = useMemo<BottomSheetCtxValue>(
    () => ({
      applySafeArea,
      requestClose,
      isContentSized: contentSized,
      bodyOverflowing,
      setBodyOverflowing,
      hasBody,
      setHasBody,
    }),
    [applySafeArea, requestClose, contentSized, bodyOverflowing, hasBody],
  );

  return (
    <BottomSheetRootPrimitive
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={snapPoints}
      activeSnapPoint={activeSnapPoint}
      setActiveSnapPoint={setActiveSnapPoint}
      dismissible={dismissible}
      overlay={overlay}
      avoidKeyboard={avoidKeyboard}
      handleOnly={handleOnly}
      haptics={haptics}
      testID={testID}
      // `shadow-bottombar` draws on native builds too (the native shadow table)
      contentClassName={cn(
        "bg-luce-neutral-background-default rounded-t-radius-lg shadow-bottombar",
        className,
      )}
    >
      {/* Context provider lives INSIDE the primitive so it crosses the native
          portal: the native primitive re-roots children to a PortalHost, which
          would otherwise leave compound parts without this context. */}
      <Ctx.Provider value={ctxValue}>
        {showHandle ? <Handle /> : null}
        {/* The name of a sheet with no visible Title. Web: the dialog's title,
            which also stops Radix warning that it has none. Native: a heading
            screen readers reach first. sr-only's -1px margin puts it outside
            the sheet, where VoiceOver and TalkBack drop it, so native keeps it
            in the corner with transparent text. */}
        {accessibilityLabel ? (
          <BottomSheetTitlePrimitive className="native:m-0 native:text-transparent sr-only">
            {accessibilityLabel}
          </BottomSheetTitlePrimitive>
        ) : null}
        {children}
      </Ctx.Provider>
    </BottomSheetRootPrimitive>
  );
};

// 57×6 (DQ13: component tokens), with 8 above and below it as Figma draws
const Handle = () => (
  <Box className="bg-luce-neutral-background-attenuated my-spacing-sm rounded-radius-full h-sheet-handle w-sheet-handle mx-auto shrink-0" />
);

const Header = ({ children, align = "left", className }: BottomSheetHeaderProps) => {
  const { isContentSized } = useCtx();
  return (
    <HeaderAlignCtx.Provider value={align}>
      <Box
        className={cn(
          "gap-spacing-sm p-spacing-lg flex flex-col border-b",
          dividerColor,
          align === "center" && "items-center",
          isContentSized && "shrink-0",
          className,
        )}
      >
        {children}
      </Box>
    </HeaderAlignCtx.Provider>
  );
};

const Title = ({ children, className }: BottomSheetTitleProps) => {
  const align = useContext(HeaderAlignCtx);
  return (
    <BottomSheetTitlePrimitive asChild>
      <Typography
        variant="label-md"
        color="neutral-foreground-default"
        align={align === "center" ? "center" : undefined}
        className={className}
      >
        {children}
      </Typography>
    </BottomSheetTitlePrimitive>
  );
};

const Description = ({ children, className }: BottomSheetDescriptionProps) => {
  const align = useContext(HeaderAlignCtx);
  return (
    <BottomSheetDescriptionPrimitive asChild>
      <Typography
        variant="body-sm"
        color="neutral-foreground-intermediate"
        align={align === "center" ? "center" : undefined}
        className={className}
      >
        {children}
      </Typography>
    </BottomSheetDescriptionPrimitive>
  );
};

const Body = ({ children, className }: BottomSheetBodyProps) => {
  const { isContentSized, setBodyOverflowing, setHasBody } = useCtx();
  // Tells the Footer a Body sits above it (see Footer)
  useEffect(() => {
    setHasBody(true);
    return () => setHasBody(false);
  }, [setHasBody]);
  // A content-sized sheet stops growing at its cap; its Body scrolls from there
  // (and the Footer shows its divider)
  if (isContentSized) {
    return (
      <SheetScrollBody className={className} onOverflowChange={setBodyOverflowing}>
        {children}
      </SheetScrollBody>
    );
  }
  return (
    <Box className={cn("gap-spacing-sm p-spacing-lg flex flex-col", className)}>{children}</Box>
  );
};

const Footer = ({ children, layout = "stacked", className }: BottomSheetFooterProps) => {
  const { applySafeArea, isContentSized, bodyOverflowing, hasBody } = useCtx();
  const insets = useSafeAreaInsets();
  // Footer padding is a uniform 16px; the bottom grows by the safe-area inset
  // so buttons clear the home indicator. On web insets.bottom is 0 (no-op).
  const extraBottomPx = applySafeArea ? insets.bottom : 0;
  // One 16px gap at each section boundary (DQ12): below a Body with no divider
  // between them, the Body's own bottom padding is that gap
  const followsBody = hasBody && !bodyOverflowing;
  return (
    <Box
      className={cn(
        "gap-spacing-sm p-spacing-lg flex",
        layout === "row" ? "flex-row items-center justify-end" : "flex-col items-stretch",
        bodyOverflowing && cn("border-t", dividerColor),
        followsBody && "pt-0",
        isContentSized && "shrink-0",
        className,
      )}
      style={{ paddingBottom: spacing.lg + extraBottomPx }}
    >
      {children}
    </Box>
  );
};

/**
 * BottomSheet.Close
 *
 * Closes the sheet via `requestClose` from context — wired directly rather
 * than through the primitive `Close`, mirroring `Dialog.Close`. On native this
 * sets `open=false`, which animates the sheet out before unmounting.
 *
 * - `asChild` — merges the close handler onto a single element child; the
 *   child's own `onClick` still runs first.
 * - children — wraps the content in a Pressable, named by its content or by
 *   `accessibilityLabel` (which an icon-only child needs).
 *
 * `disabled` stops the close in both modes.
 */
// Exported here, not in the list below, so the docs attach its API table to
// `Close` (react-docgen-typescript otherwise names it from the displayName
// assignments that follow, which aren't names in this file)
export const Close = ({
  asChild,
  children,
  className,
  accessibilityLabel,
  disabled = false,
}: BottomSheetCloseProps) => {
  const { requestClose } = useCtx();

  if (asChild) {
    if (!isValidElement(children)) {
      console.warn("BottomSheet.Close with `asChild` requires a single React element child.");
      return null;
    }
    const child = children as ReactElement<{
      onClick?: () => void;
      className?: string;
      disabled?: boolean;
    }>;
    return cloneElement(child, {
      className: cn(child.props.className, className),
      disabled: disabled || child.props.disabled,
      onClick: () => {
        if (disabled) return;
        child.props.onClick?.();
        requestClose();
      },
    });
  }

  if (children == null) return null;

  return (
    <Pressable
      onClick={requestClose}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      className={className}
    >
      {children}
    </Pressable>
  );
};

Root.displayName = "BottomSheet.Root";
Header.displayName = "BottomSheet.Header";
Title.displayName = "BottomSheet.Title";
Description.displayName = "BottomSheet.Description";
Body.displayName = "BottomSheet.Body";
Footer.displayName = "BottomSheet.Footer";
Close.displayName = "BottomSheet.Close";

export { Root, Header, Title, Description, Body, Footer };
